import { Instagram as InstagramIcon, Play } from "lucide-react";

import {
  INSTAGRAM_HANDLE,
  INSTAGRAM_REELS_URL,
  INSTAGRAM_URL,
  formatPostedAt,
  openInstagramLink,
  postImage,
  shortCaption,
  type InstagramHighlight,
  type InstagramPost,
} from "@/lib/instagram";
import type { Locale } from "@/lib/i18n";

/** Extern Instagram-länk som bryter ur eventuell iframe och öppnas i ny flik.
 *  Instagram blockerar inbäddning (ERR_BLOCKED_BY_RESPONSE), så vi simulerar
 *  en ren <a target="_blank">-klick när siten visas i en iframe. */
function InstagramExternalLink({
  href,
  className,
  children,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={(e) => {
        if (typeof window !== "undefined" && window.self !== window.top) {
          e.preventDefault();
          openInstagramLink(href);
        }
      }}
    >
      {children}
    </a>
  );
}

const COPY = {
  sv: {
    follow: "Följ oss på Instagram",
    handle: `@${INSTAGRAM_HANDLE}`,
    reels: "Reels",
    posts: "Senaste inläggen",
    viewOn: "Se på Instagram",
    watch: "Se reel",
    empty: "Flödet uppdateras inom kort.",
  },
  en: {
    follow: "Follow us on Instagram",
    handle: `@${INSTAGRAM_HANDLE}`,
    reels: "Reels",
    posts: "Latest posts",
    viewOn: "View on Instagram",
    watch: "Watch reel",
    empty: "The feed will update shortly.",
  },
  es: {
    follow: "Síguenos en Instagram",
    handle: `@${INSTAGRAM_HANDLE}`,
    reels: "Reels",
    posts: "Últimas publicaciones",
    viewOn: "Ver en Instagram",
    watch: "Ver reel",
    empty: "El contenido se actualizará pronto.",
  },
} as const;

export function InstagramCard({
  post,
  locale,
  className = "",
}: {
  post: InstagramPost;
  locale: Locale;
  className?: string;
}) {
  const image = postImage(post);
  const caption = shortCaption(post.caption, 110);
  return (
    <InstagramExternalLink
      href={post.permalink || INSTAGRAM_URL}
      className={`group relative block overflow-hidden rounded-xl border border-border bg-sand ${className}`}
    >
      {image ? (
        <img
          src={image}
          alt={caption || COPY[locale].handle}
          loading="lazy"
          decoding="async"
          className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
      ) : (
        <div className="aspect-square w-full bg-muted" />
      )}

      {post.isReel ? (
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-background/85 px-2 py-1 text-[11px] font-medium backdrop-blur">
          <Play className="size-3" aria-hidden />
          {COPY[locale].reels}
        </span>
      ) : null}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
        <p className="line-clamp-3 text-xs leading-snug text-white">{caption}</p>
        <p className="mt-1 text-[11px] text-white/70">{formatPostedAt(post.postedAt, locale)}</p>
      </div>
    </InstagramExternalLink>
  );
}

export function InstagramFollowButton({ locale }: { locale: Locale }) {
  return (
    <InstagramExternalLink
      href={INSTAGRAM_URL}
      className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
    >
      <InstagramIcon className="size-4" aria-hidden />
      {COPY[locale].follow}
    </InstagramExternalLink>
  );
}

/** Sektion för startsidan – ett urval av de senaste inläggen. */
export function InstagramSection({
  posts,
  locale,
  href,
}: {
  posts: InstagramPost[];
  locale: Locale;
  href: string;
}) {
  if (posts.length === 0) return null;
  const t = COPY[locale];
  const visible = posts.slice(0, 8);

  return (
    <section className="border-y border-border bg-background py-14 sm:py-20">
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-medium tracking-wide text-primary uppercase">Instagram</p>
            <h2 className="mt-2 font-display text-2xl font-semibold sm:text-3xl">
              {locale === "en" ? "Life at the school, day by day" : locale === "es" ? "El día a día del colegio" : "Skolans vardag, dag för dag"}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
               {locale === "en"
                ? "Photos and reels from lessons, breaks, sport and school events on Costa del Sol."
                 : locale === "es"
                   ? "Fotos y reels de clases, recreos, deporte y actividades del colegio en la Costa del Sol."
                : "Bilder och reels från lektioner, raster, idrott och skolans evenemang på Costa del Sol."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={href}
              className="text-sm font-medium text-primary link-quiet"
            >
              {locale === "en" ? "See more" : locale === "es" ? "Ver más" : "Se mer"}
            </a>
            <InstagramFollowButton locale={locale} />
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {visible.map((post) => (
            <InstagramCard key={post.id} post={post} locale={locale} />
          ))}
        </div>

        <p className="mt-6 text-sm text-muted-foreground">
          {t.handle} ·{" "}
          <InstagramExternalLink
            className="text-primary link-quiet"
            href={INSTAGRAM_REELS_URL}
          >
            {t.reels}
          </InstagramExternalLink>
        </p>
      </div>
    </section>
  );
}

/** Hela flödet med reels, teman och rutnät – används på /instagram. */
export function InstagramPageContent({
  posts,
  highlights,
  locale,
}: {
  posts: InstagramPost[];
  highlights: InstagramHighlight[];
  locale: Locale;
}) {
  const t = COPY[locale];
  const reels = posts.filter((p) => p.isReel).slice(0, 8);
  const byId = new Map(posts.map((p) => [p.id, p]));

  return (
    <div className="mx-auto max-w-6xl space-y-16 px-5 py-12 sm:py-16">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-border bg-sand p-6">
        <div>
          <p className="font-display text-lg font-semibold">{t.handle}</p>
          <p className="mt-1 text-sm text-muted-foreground">
             {locale === "en"
              ? "Everything we share on Instagram, collected in one place."
               : locale === "es"
                 ? "Todo lo que compartimos en Instagram, reunido en un solo lugar."
              : "Allt vi delar på Instagram, samlat på ett ställe."}
          </p>
        </div>
        <InstagramFollowButton locale={locale} />
      </div>

      {reels.length > 0 ? (
        <section>
          <h2 className="font-display text-xl font-semibold sm:text-2xl">{t.reels}</h2>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {reels.map((post) => (
              <InstagramCard key={post.id} post={post} locale={locale} />
            ))}
          </div>
        </section>
      ) : null}

      {highlights.map((highlight) => {
        const items = highlight.postIds
          .map((id) => byId.get(id))
          .filter((p): p is InstagramPost => Boolean(p));
        if (items.length === 0) return null;
        const highlightTitle =
          locale === "sv"
            ? highlight.titleSv
            : locale === "es"
              ? highlight.titleEs || highlight.titleEn || highlight.titleSv
              : highlight.titleEn || highlight.titleSv;
        const highlightDescription =
          locale === "sv"
            ? highlight.descriptionSv
            : locale === "es"
              ? highlight.descriptionEs || highlight.descriptionEn || highlight.descriptionSv
              : highlight.descriptionEn || highlight.descriptionSv;
        return (
          <section key={highlight.id}>
            <h2 className="font-display text-xl font-semibold sm:text-2xl">
              {highlightTitle}
            </h2>
            {highlightDescription ? (
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                {highlightDescription}
              </p>
            ) : null}
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {items.map((post) => (
                <InstagramCard key={post.id} post={post} locale={locale} />
              ))}
            </div>
          </section>
        );
      })}

      <section>
        <h2 className="font-display text-xl font-semibold sm:text-2xl">{t.posts}</h2>
        {posts.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">{t.empty}</p>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {posts.map((post) => (
              <InstagramCard key={post.id} post={post} locale={locale} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const FOLLOW_COPY: Record<
  Locale,
  { eyebrow: string; title: string; body: string; button: string; grid: string }
> = {
  sv: {
    eyebrow: "Instagram",
    title: "Följ oss på Instagram",
    body:
      "Få bilder och reels från skolan, idrotten och livet på Costa del Sol direkt i flödet. Det är ofta där vi delar det senaste först.",
    button: "Följ @swedish_international_school",
    grid: "Senaste inläggen",
  },
  en: {
    eyebrow: "Instagram",
    title: "Follow us on Instagram",
    body:
      "Get photos and reels from the school, sports and life on the Costa del Sol right in your feed. That’s often where we share the latest news first.",
    button: "Follow @swedish_international_school",
    grid: "Latest posts",
  },
  es: {
    eyebrow: "Instagram",
    title: "Síguenos en Instagram",
    body:
      "Recibe fotos y reels del colegio, el deporte y la vida en la Costa del Sol directamente en tu feed. Suele ser el primer lugar donde compartimos las novedades.",
    button: "Seguir a @swedish_international_school",
    grid: "Últimas publicaciones",
  },
};

/** CTA-block att lägga på blogg-/nyhetssidor istället för metatext. */
export function InstagramFollowCta({
  posts,
  locale,
}: {
  posts?: InstagramPost[];
  locale: Locale;
}) {
  const t = FOLLOW_COPY[locale];
  const items = (posts ?? []).slice(0, 4);
  return (
    <section className="mx-auto max-w-6xl px-5">
      <div className="overflow-hidden rounded-2xl border border-border bg-sand">
        <div className="grid gap-8 p-6 sm:p-10 lg:grid-cols-2 lg:items-center lg:gap-12">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">{t.eyebrow}</p>
            <h2 className="mt-3 text-2xl font-semibold sm:text-3xl">{t.title}</h2>
            <p className="mt-3 max-w-xl leading-relaxed text-muted-foreground">{t.body}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <InstagramFollowButton locale={locale} />
              <InstagramExternalLink
                href={INSTAGRAM_URL}
                className="text-sm font-medium text-primary link-quiet"
              >
                {t.button}
              </InstagramExternalLink>
            </div>
          </div>

          {items.length > 0 ? (
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {t.grid}
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
                {items.map((post) => (
                  <InstagramCard key={post.id} post={post} locale={locale} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
